<template>
  <BaseDialog
    v-loading="loading"
    :title="displayText.title"
    :visible="true"
    width="700px"
    :close-on-click-modal="false"
    @close="$emit('close')"
    @cancel="$emit('close')"
    @confirm="onSubmit"
  >
    <!-- 搜尋區 -->
    <header class="grid grid-cols-2 gap-[16px] mb-[20px]">
      <BaseElInput
        v-model="search.name"
        :placeholder="displayText.search.placeholder"
        clearable
      >
        <i slot="suffix" class="el-input__icon el-icon-search" />
      </BaseElInput>
      <BaseElSelect
        v-model="search.type"
        placeholder="選擇通知類別"
        clearable
      >
        <BaseElSelectOption
          v-for="type in typeOptions"
          :key="type.value"
          :value="type.value"
          :label="type.label"
        />
      </BaseElSelect>
    </header>

    <!-- 列表區 -->
    <main>
      <div
        class="grid-container text-primary-100 pt-[21px] pb-[16px]"
      >
        <BaseElCheckbox
          v-model="isAllSelected"
          :indeterminate="isIndeterminate"
          :label="displayText.table.label"
          :disabled="!multiple"
          @change="toggleAll"
        >
          <p class="text-primary-100">{{ displayText.table.eventName }}</p>
        </BaseElCheckbox>
        <p class="text-center">{{ displayText.table.type }}</p>
      </div>

      <BaseElCheckboxGroup v-model="selectedIds">
        <div
          v-for="event in displayData[tableOptions.page - 1]"
          :key="event.id"
        >
          <hr>
          <div class="grid-container" style="padding: 16px 0;">
            <BaseElCheckbox :label="event.id">
              <div class="inline-flex items-center relative" style="gap: 4px; bottom: -2px">
                <span class="truncate">{{ event.label }}</span>
              </div>
            </BaseElCheckbox>
            <p class="text-center">
              {{ event.typeLabel }}
            </p>
          </div>
        </div>
      </BaseElCheckboxGroup>
    </main>

    <!-- 分頁區 -->
    <div>
      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="flattenDeep(displayData).length"
      />
    </div>
  </BaseDialog>
</template>

<script>
import { defineComponent, onMounted, reactive, computed } from 'vue'
import { chunk, flattenDeep } from 'lodash'
import { useTable } from '@/use/table'
import { useTableBatchSelection } from '@/use/useTableBatchSelection'
import { i18n } from '@/plugins/i18n/i18n'
import BaseDialog from '../Dialog/BaseDialog.vue'

export default defineComponent({
  name: 'LinePushEventSelect',
  components: {
    BaseDialog,
  },
  props: {
    multiple: { type: Boolean, default: true },
    max: { type: [Number, null], default: null },
    selectedData: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    typeOptions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm'],
  setup (props, { emit }) {
    const { tableOptions } = useTable()
    tableOptions.pageLimit = 8

    const search = reactive({
      name: null,
      type: null,
    })

    /**
     * 過濾後的顯示資料（分頁）
     */
    const displayData = computed(() => {
      let data = [...props.data]

      // 名稱搜尋
      if (search.name) {
        data = data.filter(i =>
          i.label.includes(search.name) ||
          i.typeLabel.includes(search.name),
        )
      }

      // 類型篩選
      if (search.type) {
        data = data.filter(i => i.type === search.type)
      }

      return chunk(data, tableOptions.pageLimit)
    })

    // 使用 useTableBatchSelection 管理選取狀態
    const {
      selectedIds,
      isAllSelected,
      isIndeterminate,
      toggleAll,
      toggleRow,
    } = useTableBatchSelection({
      getKey: (event) => event.id, // 使用 id (keys.join(',')) 作為唯一鍵
      getCurrentPageRows: () => displayData.value[tableOptions.page - 1] || [],
      clearOnPageChange: false,
    })

    const displayText = computed(() => ({
      title: '選擇通知類型',
      search: {
        placeholder: '請輸入通知事件名稱',
      },
      table: {
        label: '通知事件',
        eventName: '通知事件',
        type: '通知類別',
      },
      button: {
        cancel: i18n.t('common.button.cancel.text'),
        confirm: i18n.t('common.button.confirm.text'),
      },
    }))

    const onSubmit = () => {
      // 從 id 找回原始的 keys 陣列
      const selectedKeysArrays = selectedIds.value.map(id => {
        const event = props.data.find(e => e.id === id)
        return event ? event.keys : []
      }).filter(keys => keys.length > 0)

      // 處理 max 限制
      if (props.max && selectedKeysArrays.length > props.max) {
        selectedKeysArrays.splice(props.max)
      }

      // 發送二維陣列: [['key1', 'key2'], ['key3']]
      emit('confirm', selectedKeysArrays)
      emit('close')
    }

    /**
     * 初始化選中狀態
     * 接收二維陣列: [['key1', 'key2'], ['key3']]
     */
    const initializeSelection = () => {
      if (!props.selectedData || props.selectedData.length === 0) return

      const allEvents = flattenDeep(displayData.value)

      // 將傳入的二維陣列轉為 id (join) 進行比對
      props.selectedData.forEach(keysArray => {
        const targetId = Array.isArray(keysArray) ? keysArray.join(',') : keysArray
        const event = allEvents.find(e => e.id === targetId)
        if (event) {
          toggleRow(event)
        }
      })
    }

    onMounted(() => {
      initializeSelection()
    })

    return {
      flattenDeep,
      onSubmit,
      search,
      displayData,
      tableOptions,
      displayText,
      selectedIds,
      isAllSelected,
      isIndeterminate,
      toggleAll,
    }
  },
})
</script>

<style scoped lang="postcss">
.grid-container {
  @apply grid gap-[20px];
  @apply text-normal;
  grid-template-columns: 1fr 200px;
}
::v-deep .el-input {
  @apply w-full;
}
::v-deep .el-checkbox {
  @apply w-[220px];
}
::v-deep .el-checkbox .el-checkbox__label {
  @apply truncate w-full;
}
</style>
